import cx from 'classnames';
import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import CheckBox from '@app/components/CheckBox';
import { CharacterCountVisibleTextarea } from '@app/components/Form';
import DocumentRole from '@app/store/documents/DocumentRole';
import { modalClose } from '@app/store/modal/actions';
import { getName } from '@app/store/user/user/selectors';

import './CancelSigningModal.scss';

interface CancelSigningModalProps {
  documentId: string;
  documentRole: DocumentRole;
  participantId: string;
  onCancelSigning: (params: {
    documentId: string;
    participantId: string;
    message: string;
    isDocumentBlock: boolean;
  }) => void;
}

const defaultProps = {
  documentId: null,
  documentRole: null,
  participantId: null,
  onCancelSigning() {},
};

export function CancelSigningModal({
  documentId,
  documentRole,
  participantId,
  onCancelSigning,
}: CancelSigningModalProps) {
  const [isDocumentBlock, setIsDocumentBlock] = useState(false);
  const [value, setValue] = useState('');
  const [validityMessage, setValidityMessage] = useState('');
  const [validityOn, setValidityOn] = useState(false);

  const userName = useSelector(getName);

  const { t } = useTranslation();

  const dispatch = useDispatch();

  const handleBlur = () => {
    setValidityOn(true);
  };

  const handleChange = (value: string) => {
    setValue(value);
    if (value.length < 2) {
      setValidityMessage(t('CancelSigningModal-errorMessage'));
    } else {
      setValidityMessage('');
    }
  };

  const handleCheckClick = () => {
    setIsDocumentBlock(!isDocumentBlock);
  };

  const onCancel = () => dispatch(modalClose());

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    handleBlur();
    handleChange(value);
    if (value.length >= 2) {
      onCancel();
      onCancelSigning({
        documentId,
        participantId,
        message: value,
        isDocumentBlock,
      });
    }
  };

  return (
    <div className="Container Container--max-xxs">
      <div className="Card Card--pop box-6x align-center">
        <h4 className="space-3x">
          <strong>{t('CancelSigningModal-title')}</strong>
        </h4>
        <p className="space-4x pre-wrap">{t('CancelSigningModal-description')}</p>
        <div>
          <form onSubmit={handleSubmit}>
            <div
              className={cx('InputSet', 'space-4x', {
                'InputSet--invalid': validityOn && validityMessage,
              })}
            >
              <CharacterCountVisibleTextarea
                name="comment"
                placeholder={t('CancelSigningModal-placeholder')}
                value={value}
                onBlur={handleBlur}
                onChange={handleChange}
              />
              {validityOn && validityMessage && (
                <p className="InputSet__invalidity-message">{validityMessage}</p>
              )}
            </div>
            {documentRole === DocumentRole.REQUESTER && (
              <div // eslint-disable-line
                onClick={handleCheckClick}
              >
                <CheckBox
                  className="CancelSigningModal__document-block-check"
                  isChecked={isDocumentBlock}
                  onClick={handleCheckClick}
                />
                <p className="CancelSigningModal__document-block-info space-4x">
                  {t('CancelSigningModal-additionalDescriptionForRequester', {
                    userName,
                  })}
                </p>
              </div>
            )}
            <div>
              <button className="Btn" type="button" onClick={onCancel}>
                {t('CancelSigningModal-cancelButton')}
              </button>
              <button className="Btn Btn--primary" type="submit">
                {t('CancelSigningModal-submitButton')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

CancelSigningModal.defaultProps = defaultProps;

export default CancelSigningModal;
